import { get, isEmpty, isNull } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Alert, Header, SpaceBetween } from '@amzn/awsui-components-react';
import Button from '@amzn/awsui-components-react/polaris/button';
import { push } from 'connected-react-router';
import Table from '../../../../common/components/table/components/Table';
import surveyTableConfig, { checkSurveyRequest } from '../constants/survey_config';
import { searchSurveysAction } from '../redux/surveys';
import { checkSurveysAction } from '../redux/createSurveyChecker';
import { getStudyPeriod } from '../../../../common/constants/study_period';
import { getRoutePathWithStudyPeriod, ROUTE_PATH } from '../../../../common/constants/route_paths';
import { SUPPORTED_IMPORT_TYPES } from '../../../import_data/constants/import_types';
import {
  getImportFailureNotification,
  getImportTypeMetadataForHeader,
  processUserLevelImportMetadata,
} from '../../../import_data/utils/import_utils';
import { setPageNotification } from '../../../../common/components/with_page/redux/reducer';
import { getImportMetadataAction } from '../../../import_data/redux/GetImportMetadata';
import { surveyGroupStatusAction } from '../../../survey/redux/surveyGroupStatus';
import { getDateInMMDDYYYYHHMIFormat } from '../../../../common/utils/date_time_util';
import { SURVEY_TYPE } from '../../../../common/components/pdf_generator/templates/SurveyPDFTemplate/utils/DataUtils';

const RiseSurveyTable = props => {
  const {
    checkSurveyPresent,
    isFetchingSurveys,
    totalSurveys,
    studyPeriod,
    surveyType,
    navigateTo,
    fetchGroupStatus,
    isFetchingGroupStatus,
    groupStatusData,
    schema,
  } = props;

  const SURVEY_SCHEMA_MAP = {
    [SURVEY_TYPE.LABOR_BC_SUBSTANTIATION]: ['bcSubstantiationSurveys', 'bcSubstantiationSurveys'],
    [SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION]: [
      'projectSubstantiationSurveys',
      'projectSubstantiationSurveys',
    ],
  };

  const [isNoSurveysAlertVisible, setNoSurveysAlertVisible] = useState(false);

  const ALERT_CONFIG = {
    InProgress: { type: 'info', statusIconAriaLabel: 'Info' },
    Completed: { type: 'success', statusIconAriaLabel: 'Success' },
    Error: { type: 'error', statusIconAriaLabel: 'Error' },
  };

  useEffect(() => {
    const enrichedCheckSurveyRequest = {
      ...checkSurveyRequest,
      body: {
        ...checkSurveyRequest.body,
        surveyType,
      },
    };
    checkSurveyPresent(enrichedCheckSurveyRequest);
    // eslint-disable-next-line
  }, [studyPeriod, surveyType]);

  useEffect(() => {
    if (surveyType) {
      fetchGroupStatus({ body: { surveyType } });
    }
  }, [surveyType, fetchGroupStatus]);

  useEffect(() => {
    setNoSurveysAlertVisible(totalSurveys === 0 && !isFetchingSurveys);
  }, [totalSurveys, isFetchingSurveys]);

  const createSurveyHandler = () => {
    navigateTo({
      pathname: getRoutePathWithStudyPeriod(ROUTE_PATH.CREATE_RISE_SURVEY, studyPeriod),
    });
  };

  const { importMetadata, pushNotification } = props;
  const supportedImportType = SUPPORTED_IMPORT_TYPES.LABOR_DUAL_SURVEY;

  const [latestImportTypeMetadata, setLatestImportTypeMetadata] = React.useState(null);

  const schemaForSurveyType = get(schema, SURVEY_SCHEMA_MAP[surveyType] || []);

  const tableProps = {
    ...props,
    schema: schemaForSurveyType,
  };

  useEffect(() => {
    if (!isEmpty(importMetadata)) {
      const importTypeMetadata = getImportTypeMetadataForHeader(
        importMetadata,
        supportedImportType,
      );
      setLatestImportTypeMetadata(importTypeMetadata);

      const userLevelImportMetadata = processUserLevelImportMetadata(importMetadata);
      if (!isNull(userLevelImportMetadata)) {
        pushNotification(
          getImportFailureNotification({
            tableIdentifier: 'RiseSurveyTable',
            ...userLevelImportMetadata,
            supportedImportTypes: supportedImportType,
          }),
        );
      }
    }
  }, [importMetadata, supportedImportType, pushNotification]);

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Substantiation Surveys</Header>
      {!isFetchingGroupStatus &&
        groupStatusData &&
        ['InProgress', 'Completed', 'Error'].includes(groupStatusData.surveyConfigurationStatus) &&
        (() => {
          const { type, statusIconAriaLabel } = ALERT_CONFIG[
            groupStatusData.surveyConfigurationStatus
          ];
          return (
            <Alert
              type={type}
              statusIconAriaLabel={statusIconAriaLabel}
              header="Survey Creation Status"
              dismissible={false}
            >
              <div>Created By: {groupStatusData.createdBy}</div>
              <div>Status: {groupStatusData.surveyConfigurationStatus}</div>
              <div>Survey Template: {groupStatusData.templateDocumentId}</div>
              <div>
                Original Release Date: {getDateInMMDDYYYYHHMIFormat(groupStatusData.releaseDate)}
              </div>
            </Alert>
          );
        })()}
      <Alert
        visible={isNoSurveysAlertVisible && surveyType !== SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION}
        dismissAriaLabel="Close alert"
        type="warning"
        action={
          <Button variant="primary" onClick={createSurveyHandler}>
            Create survey
          </Button>
        }
      >
        We don&apos;t have any survey. Create survey(s) for the SMEs.
      </Alert>
      <Table
        {...tableProps}
        config={surveyTableConfig({ importTypeMetadata: latestImportTypeMetadata, surveyType })}
      />
    </SpaceBetween>
  );
};

RiseSurveyTable.propTypes = {
  schema: PropTypes.array.isRequired,
  data: PropTypes.object.isRequired,
  isFetchingData: PropTypes.bool.isRequired,
  getDataBegin: PropTypes.func.isRequired,
  clearData: PropTypes.func.isRequired,
  totalResultCount: PropTypes.number.isRequired,
  totalSurveys: PropTypes.number.isRequired,
  isFetchingSurveys: PropTypes.bool.isRequired,
  checkSurveyPresent: PropTypes.func.isRequired,
  studyPeriod: PropTypes.string.isRequired,
  surveyType: PropTypes.string.isRequired,
  navigateTo: PropTypes.func.isRequired,
  importMetadata: PropTypes.array.isRequired,
  pushNotification: PropTypes.func.isRequired,
  getImportDataBegin: PropTypes.func.isRequired,
  getImportDataClear: PropTypes.func.isRequired,
  fetchGroupStatus: PropTypes.func.isRequired,
  isFetchingGroupStatus: PropTypes.bool.isRequired,
  groupStatusData: PropTypes.shape({
    createdBy: PropTypes.string,
    surveyConfigurationStatus: PropTypes.string,
    templateDocumentId: PropTypes.string,
    releaseDate: PropTypes.string,
  }),
};

const mapStateToProps = state => {
  return {
    schema: get(state, 'entities.schemas.data.pages', []) || [],
    data: get(state, 'entities.surveys.data', {}) || {},
    totalResultCount: get(state, 'entities.surveys.data.totalResultCount', 0) || 0,
    isFetchingData: get(state, 'entities.surveys.isFetching', false) || false,
    totalSurveys: get(state, 'entities.checkSurveys.data.totalResultCount', 0) || 0,
    isFetchingSurveys: get(state, 'entities.checkSurveys.isFetching', false) || false,
    studyPeriod: getStudyPeriod(state),
    importMetadata: get(state, 'entities.getImportMetadata.data.body.importMetadata', []) || [],
    studyType: get(state, 'studyType.studyType'),
    isFetchingGroupStatus: get(state, 'entities.surveyGroupStatus.isFetching', false),
    groupStatusData: get(state, 'entities.surveyGroupStatus.data', null),
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getDataBegin: searchSurveysAction.BEGIN,
      checkSurveyPresent: checkSurveysAction.BEGIN,
      clearData: searchSurveysAction.CLEAR,
      navigateTo: payload => push(payload),
      pushNotification: setPageNotification,
      getImportDataBegin: getImportMetadataAction.BEGIN,
      getImportDataClear: getImportMetadataAction.CLEAR,
      fetchGroupStatus: surveyGroupStatusAction.BEGIN,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(RiseSurveyTable);
